"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { Check, Star, CreditCard } from "lucide-react";
import { cn } from "@/lib/utils";
import { BASE_URL } from "@/lib/api";
import { buttonVariants } from "@/components/ui/button";
import { PriceTag } from "@/components/marketing/PriceTag";
import { track } from "@/lib/analytics";
import { useTranslation } from "@/lib/i18n";

/* Compact pricing preview for the landing page. Pulls the live, sadmin-driven
   plans from the same endpoint the full /pricing page uses, falling back to the
   shared defaults so it always renders. Kept intentionally light — four price
   cards with the top included features and a link through to full pricing. */

/* Plan shape + offline fallback live in ./plans so the ROI calculator can share
   them without pulling this (separately code-split) component into its chunk.
   Re-exported here for the existing `from "@/components/marketing/PricingPreview"`
   import sites. */
import { DEFAULT_PLANS, type ApiPlan } from "@/components/marketing/plans";
export type { ApiPlan } from "@/components/marketing/plans";

/* `initialPlans` is the sadmin PlanConfig fetched server-side in the page and
   passed down, so the first paint / no-JS / crawlers already see the real plans.
   We still re-fetch on mount to pick up edits and the visitor's currency. */
export default function PricingPreview({ initialPlans, initialCheckoutEnabled }: { initialPlans?: ApiPlan[]; initialCheckoutEnabled?: boolean }) {
  const { t } = useTranslation();
  const [plans, setPlans] = useState<ApiPlan[]>(
    initialPlans && initialPlans.length > 0 ? initialPlans : DEFAULT_PLANS,
  );
  const [checkoutEnabled, setCheckoutEnabled] = useState(!!initialCheckoutEnabled);

  useEffect(() => {
    let cancelled = false;
    fetch(`${BASE_URL}/api/platform/plans`, { cache: "no-store" })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        const { plans: arr, checkoutEnabled: co } = data as { plans?: ApiPlan[]; checkoutEnabled?: boolean };
        if (Array.isArray(arr) && arr.length > 0) setPlans(arr);
        if (typeof co === "boolean") setCheckoutEnabled(co);
      })
      .catch(() => { /* keep DEFAULT_PLANS */ });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {plans.map((plan) => {
        const included = plan.features.filter((f) => f.included).slice(0, 4);
        // Only the four built-in keys are wired through register/onboarding/
        // checkout — a custom sadmin plan key gets the contact-sales path.
        const sellable = ["starter", "growth", "pro"].includes(plan.key);
        return (
          <div
            key={plan.key}
            className={cn(
              "relative flex flex-col rounded-2xl border bg-card overflow-hidden transition-all duration-200 ease-apple hover:-translate-y-0.5 hover:shadow-soft-md",
              plan.isPopular ? "border-primary border-[1.5px] shadow-soft-md" : "border-border shadow-soft-xs",
            )}
          >
            {plan.isPopular && (
              <div className="bg-primary text-primary-foreground text-caption font-semibold uppercase tracking-wider px-3 py-1.5 flex items-center justify-center gap-1">
                <Star className="w-3 h-3" /> {t("pricing.mostPopular")}
              </div>
            )}
            <div className="p-5 flex flex-col flex-1">
              <p className="text-footnote uppercase tracking-wider text-muted-foreground font-semibold mb-2">{plan.name}</p>
              <div className="flex items-baseline gap-1 mb-1">
                <PriceTag usd={plan.price} className="text-[1.875rem] font-semibold tracking-tight text-foreground leading-none tabular-nums" />
                <span className="text-muted-foreground text-footnote">{t("pricing.perUserMo")}</span>
              </div>
              <p className="text-[10.5px] text-muted-foreground mb-4">{t("pricing.billedAnnually")}</p>
              <ul className="space-y-2 mb-5">
                {included.map((f) => (
                  <li key={f.text} className="flex items-start gap-2 text-[12.5px]">
                    <Check className="w-3.5 h-3.5 text-success shrink-0 mt-0.5" />
                    <span className="text-foreground leading-snug">{f.text}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-auto flex flex-col gap-1.5">
                <Link
                  href={sellable ? `/register?plan=${plan.key}` : "/contact"}
                  onClick={() => track("CTA: Pricing card", { plan: plan.key, location: "landing-pricing-preview" })}
                  className={cn(
                    buttonVariants({ variant: plan.isPopular ? "default" : "outline", size: "sm" }),
                    "w-full",
                  )}
                >
                  {plan.ctaText}
                </Link>
                {checkoutEnabled && sellable && (
                  <Link
                    href={`/register?plan=${plan.key}&intent=buy`}
                    onClick={() => track("CTA: Buy now", { plan: plan.key, location: "landing-pricing-preview" })}
                    className={cn(buttonVariants({ variant: "ghost", size: "sm" }), "w-full gap-1.5 text-primary hover:text-primary")}
                  >
                    <CreditCard className="w-3.5 h-3.5" /> {t("pricing.buyNow")}
                  </Link>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
